import { useMemo } from 'react';
import {
  OutgoingEventProductDto,
  OutgoingSectionDto
} from 'api/dtos/events/products';

import { useSectionManagement } from './hooks/useSectionManagement';
import { useSectionMutations } from './hooks/useSectionMutations';
import { useSectionHelpers } from './hooks/useSectionHelpers';
import { SectionsProvider } from './context/SectionsContext';
import { SectionsContent } from './components/SectionsContent';

interface SectionsProps {
  eventId: string;
  tiers?: OutgoingEventProductDto[];
  sections?: OutgoingSectionDto[];
  isEventLoading?: boolean;
  children: (
    tickets: OutgoingEventProductDto[],
    sectionId: string
  ) => React.ReactNode;
  onReorder?: (
    tiers: OutgoingEventProductDto[],
    sections: OutgoingSectionDto[]
  ) => void;
  onDeleteSection?: (sectionId: string) => void;
  addEventPricingTierToSection?: (
    sectionId: string,
    tier: OutgoingEventProductDto
  ) => void;
  setAddTierToSectionId?: (sectionId: string) => void;
}

const Sections = ({
  eventId,
  tiers,
  sections,
  isEventLoading,
  children,
  onReorder,
  onDeleteSection,
  addEventPricingTierToSection,
  setAddTierToSectionId
}: SectionsProps) => {
  const sectionManagement = useSectionManagement();
  const sectionMutations = useSectionMutations({
    eventId,
    onDeleteSection,
    onSectionUpdated: () => {
      sectionManagement.clearSectionNames();
      sectionManagement.clearSelectedSection();
    }
  });
  const derivedData = useSectionHelpers({ tiers, sections });

  const contextValue = useMemo(
    () => ({
      // Data
      eventId,
      tiers,
      sections,
      isEventLoading,
      ...derivedData,

      // Section Management State
      ...sectionManagement,

      // Mutations
      createSectionMutation: sectionMutations.createSectionMutation,
      deleteMutation: sectionMutations.deleteSectionMutation,
      updateSectionName: sectionMutations.updateSectionName,
      deleteSection: sectionMutations.deleteSection,

      // Callbacks
      onDeleteSection,
      addEventPricingTierToSection,
      setAddTierToSectionId,
      onReorder,

      // Children render prop
      children
    }),
    [
      eventId,
      tiers,
      sections,
      isEventLoading,
      derivedData,
      sectionManagement,
      sectionMutations,
      onDeleteSection,
      addEventPricingTierToSection,
      setAddTierToSectionId,
      onReorder,
      children
    ]
  );

  return (
    <SectionsProvider value={contextValue}>
      <SectionsContent />
    </SectionsProvider>
  );
};

export default Sections;
